<template>
  <fieldset
    v-loading="isAddressLoading"
    class="expand-address-section"
    element-loading-custom-class="custom-spinner-class"
    :element-loading-text="loadingMessage"
    :class="parentId"
  >
    <go-go-google-map
      v-if="showMap"
      :description="$t('address.movePinToAdjustLocation')"
      :markers="[marker]"
      :map-center="mapCenter"
      :is-draggable="isEditing"
      :map-type-id="mapTypeId"
      :zoom="zoom"
      :is-open-default="openMapDefault"
      :is-side-by-side="sideBySideMap"
      @change="setMarkerPosition"
    />

    <div
      v-if="showOrderNumber"
      class="row"
    >
      <label
        class="col-3 control-label"
        :class="isEditing ? 'pt-10' : 'pt-5'"
      > {{ $t('address.order') }}{{ required ? '*' : '' }} </label>
      <div class="col-8">
        <number-input
          :id="`locationOrder-${parentId}`"
          v-model="address.order"
          autocomplete="nope"
          class="form-control location-order"
          step="1"
          :required="required"
          min="1"
          :disabled="!isEditing"
        />
        <span
          v-if="address.order || isEditing"
          class="help-block"
        >
          {{ $t('address.orderHelper') }}
        </span>
      </div>
    </div>

    <div
      v-if="showLocationName || showPhoneNumber"
      class="row align-items-center"
    >
      <label
        v-if="showLocationName"
        class="col-sm-3 control-label"
      >
        {{ $t('address.locationName') }}{{ locationNameRequired ? '*' : '' }}
      </label>
      <div
        v-if="showLocationName"
        :class="showPhoneNumber ? 'col-sm-3' : 'col-sm-8'"
      >
        <sanitized-input
          :id="`locationName-${parentId}`"
          v-model="address.name"
          placeholder="Gym"
          :required="locationNameRequired"
          :disabled="!isEditing"
        />
      </div>

      <label
        v-if="showPhoneNumber"
        class="col-sm-2 control-label"
      >
        {{ $t('customerInfo.phoneNumber') }}
      </label>
      <phone-number-input
        v-if="showPhoneNumber"
        :id="`locationPhone-${parentId}`"
        v-model="address.phoneNumber"
        class="col-sm-3"
        :page-id="parentId"
        :show="address.phoneNumber || isEditing ? true : false"
        :disabled="!isEditing"
      />
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ billingAddress ? $t('payment.billingAddress') : $t('address.streetAddress') }}{{ required ? '*' : '' }}
      </label>
      <div class="col-sm-9">
        <address-autocomplete-input
          :id="`streetAddress-${parentId}`"
          v-model="address.streetAddress"
          :anchor-bound="anchorBound || customerAnchorBound"
          :disabled="!isEditing"
          :required="required"
          @place-changed="setAddressData"
        />
      </div>
    </div>
    <div class="row align-items-center">
      <label
        v-if="isAptFieldVisible"
        class="col-sm-1 control-label m-pt-10"
      >
        {{ $t('address.streetAddress2') }}
      </label>
      <div
        v-if="isAptFieldVisible"
        class="col-sm-2"
      >
        <sanitized-input
          :id="`aptNum-${parentId}`"
          v-model="address.streetAddress2"
          placeholder="Apt and/or building number"
          :disabled="!isEditing"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> {{ $t('address.city') }}{{ required ? '*' : '' }} </label>
      <div class="col-sm-8">
        <sanitized-input
          :id="`city-${parentId}`"
          v-model="address.city"
          :required="required"
          placeholder="Independence"
          :disabled="!isEditing"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ isFromCanada ? $t('address.provinces') : $t('address.state') }}{{ required ? '*' : '' }}
      </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`state-${parentId}`"
          v-model="address.state"
          :required="required"
          placeholder="KS"
          :disabled="!isEditing"
        />
      </div>

      <label class="control-label col-sm-2 m-pt-10">
        {{ isFromCanada ? $t('address.postal') : $t('address.zip') }}{{ required ? '*' : '' }}
      </label>
      <div class="col-sm-3">
        <sanitized-input
          :id="`zip-${parentId}`"
          v-model="address.zip"
          :required="required"
          placeholder="67301"
          :disabled="!isEditing"
        />
      </div>
    </div>

    <div
      v-if="showGeocode"
      class="row align-items-center"
    >
      <label class="col-sm-3 control-label"> {{ $t('address.latitude') }}{{ required ? '*' : '' }} </label>
      <div class="col-sm-3">
        <number-input
          :id="`latitude-${parentId}`"
          v-model="address.latitude"
          class="form-control"
          placeholder="37.2259416"
          autocomplete="nope"
          :required="required"
          :index="0"
          :disabled="!isEditing"
          @paste-url="pasteUrl"
        />
      </div>

      <label class="control-label col-sm-2"> {{ $t('address.longitude') }}{{ required ? '*' : '' }} </label>
      <div class="col-sm-3">
        <number-input
          :id="`longitude-${parentId}`"
          v-model="address.longitude"
          class="form-control"
          placeholder="-95.7380295"
          autocomplete="nope"
          :required="required"
          :index="1"
          :disabled="!isEditing"
          @paste-url="pasteUrl"
        />
      </div>
    </div>

    <div
      v-if="showGooglePlaceId"
      class="row align-items-center"
    >
      <label class="col-sm-3 control-label">
        {{ $t('address.googlePlaceId') }}
      </label>
      <div class="col-sm-8 content-view">
        {{ address.googlePlaceId }}
      </div>
    </div>

    <div
      v-if="showHomeNote"
      class="row"
    >
      <slot name="noteToDriver">
        <label
          class="col-sm-3 control-label"
          :class="{ 'pt-10': isEditing }"
        >
          {{ $t('customerInfo.rideNoteToDriver') }}
        </label>
      </slot>

      <div
        v-if="isEditing"
        class="col-sm-8"
      >
        <sanitized-textarea
          :id="`noteToDriver-${parentId}`"
          v-model="address.noteToDriver"
          autosize
          :placeholder="isEditing ? 'Example: The gate code is 2348' : 'click Edit to add a note'"
        />
      </div>
      <div
        v-else
        :id="`noteToDriver-${parentId}`"
        v-dompurify-html="util.convertTextToHtml(address.noteToDriver)"
        class="col-sm-8 content-view"
      />
    </div>

    <div
      v-if="showGourmetNote"
      class="row"
    >
      <label
        class="col-sm-3 control-label"
        :class="{ 'pt-10': isEditing }"
      >
        {{ $t('customerInfo.gourmetNoteToDriver') }}
      </label>
      <div
        v-if="isEditing"
        class="col-sm-8"
      >
        <sanitized-textarea
          :id="`gourmetNoteToDriver-${parentId}`"
          v-model="address.gourmetNoteToDriver"
          autosize
          :placeholder="isEditing ? 'The gate code is 2348' : 'click Edit to add a note'"
        />
      </div>
      <div
        v-else
        :id="`gourmetNoteToDriver-${parentId}`"
        v-dompurify-html="util.convertTextToHtml(address.gourmetNoteToDriver)"
        class="col-sm-8 content-view"
      />
    </div>

    <choose-venues-place-dialog
      v-if="isVenuesSearch"
      :is-show-dialog="isShowVenuesPlaceDialog"
      :venues-places-list="venuesPlacesList"
      :venues-place-type="index === 0 ? 'pickup' : 'dropoff'"
      @submit="chooseVenuesPlace"
      @cancel="incompleteChooseVenuesPlace"
    />
  </fieldset>
</template>

<script setup lang="ts">
import { ref, Ref, computed, WritableComputedRef, watch, PropType, defineAsyncComponent } from 'vue';

import util from '@/Utils/util';
import { sendGet } from '@/Utils/api';
import { useCustomer } from '@/use/customer';
import { GeoLocationType, CustomLocationType, AddressType } from '@/models/address';
import { Venue } from '@/models/venue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<CustomLocationType>,
    default: () => ({
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      latitude: null,
      longitude: null,
      noteToDriver: '',
      gourmetNoteToDriver: '',
      order: 0,
      name: '',
      phoneNumber: '',
      googlePlaceTypes: [],
    }),
  },

  index: {
    type: Number,
    default: 0,
  },

  isEditing: {
    type: Boolean,
    default: false,
  },

  parentId: {
    type: String,
    default: 'form',
  },

  showLocationName: {
    type: Boolean,
    default: false,
  },

  locationNameRequired: {
    type: Boolean,
    default: true,
  },

  showPhoneNumber: {
    type: Boolean,
    default: false,
  },

  showApt: {
    type: Boolean,
    default: true,
  },

  showGeocode: {
    type: Boolean,
    default: true,
  },

  showHomeNote: {
    type: Boolean,
    default: true,
  },

  showGourmetNote: {
    type: Boolean,
    default: false,
  },

  showMap: {
    type: Boolean,
    default: true,
  },

  mapTypeId: {
    type: String,
    default: 'roadmap',
  },

  zoom: {
    type: Number,
    default: 14,
  },

  showOrderNumber: {
    type: Boolean,
    default: false,
  },

  openMapDefault: {
    type: Boolean,
    default: false,
  },

  sideBySideMap: {
    type: Boolean,
    default: false,
  },

  billingAddress: {
    type: Boolean,
    default: false,
  },

  showGooglePlaceId: {
    type: Boolean,
    default: true,
  },

  required: {
    type: Boolean,
    default: true,
  },

  anchorBound: {
    type: Object as PropType<google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null>,
    default: null,
  },

  isVenuesSearch: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'set-notes']);

const { anchorBound: customerAnchorBound } = useCustomer();

const address: WritableComputedRef<CustomLocationType> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const mapCenter: Ref<GeoLocationType> = ref({
  lat: 37.42248,
  lng: -122.08547,
});
const marker: Ref<any> = ref({
  position: {
    lat: 0.0,
    lng: 0.0,
  } as GeoLocationType,
  initialPosition: {
    lat: 0.0,
    lng: 0.0,
  } as GeoLocationType,
  isDraggable: true,
  isShow: false,
  index: 0,
  icon: '/static/img/map_marker.png',
});
const isAddressLoading = ref(false);
const loadingMessage = ref('');
const isShowVenuesPlaceDialog = ref(false);
const venuesPlacesList: Ref<Venue[]> = ref([]);

const isFromCanada = computed(() => {
  return address.value.country === 'CA';
});

const isAptFieldVisible = computed(() => {
  if (!props.showApt) {
    return false;
  }

  if (props.isEditing) {
    return true;
  }

  return !!address.value.streetAddress2;
});

const pasteUrl = (geocode: any) => {
  if (geocode) {
    const updatedAddress = {
      ...address.value,
      latitude: geocode.lat,
      longitude: geocode.lng,
    };

    emit('update:modelValue', updatedAddress);
  }
};

const setAddressData = (newAddress: CustomLocationType | null) => {
  const updatedAddress = {
    ...address.value,
    streetAddress: newAddress?.streetAddress || '',
    streetAddress2: '',
    city: newAddress?.city || '',
    state: newAddress?.state || '',
    zip: newAddress?.zip || '',
    country: newAddress?.country || '',
    latitude: newAddress?.latitude ? Number(newAddress.latitude) : null,
    longitude: newAddress?.longitude ? Number(newAddress.longitude) : null,
    googlePlaceId: newAddress?.googlePlaceId || null,
    googlePlaceTypes: newAddress?.googlePlaceTypes || [],
    name: address.value.name?.trim() || newAddress?.name || '',
  };

  if (props.isVenuesSearch) {
    getVenuesPlaces(Number(updatedAddress.latitude), Number(updatedAddress.longitude));
  }

  emit('update:modelValue', updatedAddress);
};

const getVenuesPlaces = async (lat: number, lng: number) => {
  venuesPlacesList.value = [];

  try {
    const query = {
      lat,
      lng,
    };
    const response = await sendGet('venues', query);

    venuesPlacesList.value = response.data || [];

    const zones = venuesPlacesList.value[0]?.zones;
    const locations = venuesPlacesList.value[0]?.locations;
    const type = venuesPlacesList.value[0]?.type;
    const placeName = venuesPlacesList.value[0]?.name || '';
    const isAirport = type === 'airport' || placeName.toLowerCase().includes('airport');

    // For pickup address, we should always show the dialog
    if (props.index === AddressType.PickUp) {
      isShowVenuesPlaceDialog.value = true;
    } else if (props.index === AddressType.DropOff && isAirport) {
      // For dropdoff address, we should show if it is airport
      isShowVenuesPlaceDialog.value = true;
    } else if (zones || locations) {
      isShowVenuesPlaceDialog.value = true; // For multi-stop addresses, we should show if it has zones or locations
    }
  } catch (error) {
    // No need to do anything
  }
};

const setMarkerPosition = (lat: number, lng: number, index: number, isAddressChanged: boolean = false) => {
  util.updateMarkerPosition(marker.value, lat, lng, isAddressChanged);

  mapCenter.value = {
    lat: Number(lat) || mapCenter.value.lat,
    lng: Number(lng) || mapCenter.value.lng,
  };

  if (!isAddressChanged) {
    const updatedAddress = {
      ...address.value,
      latitude: lat,
      longitude: lng,
    };
    emit('update:modelValue', updatedAddress);
  }
};

const chooseVenuesPlace = (venuesData: any) => {
  isShowVenuesPlaceDialog.value = false;

  if (props.index === AddressType.PickUp) {
    emit('set-notes', venuesData.driverNotePickUp, venuesData.instruction, AddressType.PickUp);
  } else {
    emit('set-notes', venuesData.driverNoteDropOff, venuesData.instruction, AddressType.DropOff);
  }
};

const incompleteChooseVenuesPlace = (driverNotePickUp: string, driverNoteDropOff: string) => {
  isShowVenuesPlaceDialog.value = false;

  if (props.index === AddressType.PickUp) {
    emit('set-notes', driverNotePickUp, '', AddressType.PickUp);
  } else {
    emit('set-notes', driverNoteDropOff, '', AddressType.DropOff);
  }
};

watch(
  address,
  (newValue?: CustomLocationType, oldValue?: CustomLocationType) => {
    const isAddressChanged = util.isAddressChanged(newValue, oldValue);
    if (isAddressChanged) {
      setMarkerPosition(Number(newValue?.latitude), Number(newValue?.longitude), 0, true);
    }
  },
  {
    deep: true,
    immediate: true,
  },
);
</script>

<style lang="scss">
.page-content {
  .expand-address-section {
    .google-map-container .el-collapse .el-collapse-item {
      margin-top: 0;
      border: 1px solid #7ac29a;
    }
    .content-view {
      white-space: break-spaces;
    }
    .location-order {
      max-width: 100px;
      min-width: 100px;
      @media (max-width: 576px) {
        max-width: unset;
      }
    }
  }
}
</style>
